import type {
  Milestone,
  MilestoneDeliveryStatus,
  Project,
  ProjectStatus,
} from '../types';
import { isReplacedMilestone, isMilestoneSettledForClosure } from './milestoneHistory';

export type ConflictDeliveryStatus =
  | 'under_resolution'
  | 'disputed'
  | 'conflict'
  | 'external_dispute';

export type ProjectDisplayStatus = ProjectStatus | MilestoneDeliveryStatus;

const conflictSeverity: Record<ConflictDeliveryStatus, number> = {
  under_resolution: 1,
  disputed: 2,
  conflict: 3,
  external_dispute: 4,
};

function conflictSeverityFor(milestone: Milestone): number {
  if (isReplacedMilestone(milestone) || milestone.continuedBy) return 0;
  if (
    milestone.conflictStartedAt &&
    !['jointly_attested', 'payout_processing', 'paid'].includes(
      milestone.deliveryStatus ?? '',
    )
  ) {
    return Math.max(
      milestone.deliveryStatus && isConflictDeliveryStatus(milestone.deliveryStatus)
        ? conflictSeverity[milestone.deliveryStatus]
        : 0,
      3,
    );
  }
  return milestone.deliveryStatus && isConflictDeliveryStatus(milestone.deliveryStatus)
    ? conflictSeverity[milestone.deliveryStatus]
    : 0;
}

export function isConflictDeliveryStatus(
  status: string | null | undefined,
): status is ConflictDeliveryStatus {
  return !!status && status in conflictSeverity;
}

function updatedAtValue(milestone: Milestone): number {
  const value = Date.parse(milestone.updatedAt);
  return Number.isFinite(value) ? value : 0;
}

function newestMilestone(left: Milestone, right: Milestone): number {
  return (
    updatedAtValue(right) - updatedAtValue(left) ||
    right.milestoneNumber - left.milestoneNumber ||
    right.id.localeCompare(left.id)
  );
}

/**
 * A project's visible conflict state must never be softened by newer activity
 * in another contract. Within the same conflict level, the latest event wins.
 */
export function dominantConflictMilestone(
  milestones: Milestone[],
): Milestone | null {
  return (
    milestones
      .filter((milestone) => conflictSeverityFor(milestone) > 0)
      .sort((left, right) => {
        const severityDifference =
          conflictSeverityFor(right) - conflictSeverityFor(left);
        return severityDifference || newestMilestone(left, right);
      })[0] ?? null
  );
}

/** The contract timeline follows the first unfinished stage unless a conflict
 * anywhere in that contract has higher priority. */
export function currentContractWorkflowMilestone(
  milestones: Milestone[],
): Milestone | null {
  const sorted = milestones.filter(milestone => !isReplacedMilestone(milestone) && !milestone.continuedBy).sort(
    (left, right) => left.milestoneNumber - right.milestoneNumber,
  );
  return (
    dominantConflictMilestone(sorted) ??
    sorted.find((milestone) => !isMilestoneSettledForClosure(milestone)) ??
    sorted[sorted.length - 1] ??
    null
  );
}

/**
 * Project badges follow the most serious active conflict across all contracts.
 * Without a conflict, they follow the latest active milestone event. A newly
 * created, not-started milestone must not hide the project's lifecycle status.
 */
export function projectWorkflowStatus(
  projectStatus: ProjectStatus,
  milestones: Milestone[],
  serverWorkflowStatus?: MilestoneDeliveryStatus | null,
): ProjectDisplayStatus {
  milestones = milestones.filter(milestone => !isReplacedMilestone(milestone) && !milestone.continuedBy);
  const dominantConflict = dominantConflictMilestone(milestones);
  if (dominantConflict?.deliveryStatus) {
    return dominantConflict.conflictStartedAt &&
      !['conflict', 'external_dispute'].includes(
        dominantConflict.deliveryStatus,
      )
      ? 'conflict'
      : dominantConflict.deliveryStatus;
  }
  if (isConflictDeliveryStatus(serverWorkflowStatus)) {
    return serverWorkflowStatus;
  }

  if (projectStatus === 'completed') return projectStatus;

  const latestActiveMilestone = milestones
    .filter(
      (milestone) =>
        !!milestone.deliveryStatus &&
        !['not_started', 'paid'].includes(milestone.deliveryStatus),
    )
    .sort(newestMilestone)[0];
  if (latestActiveMilestone?.deliveryStatus) {
    return latestActiveMilestone.deliveryStatus;
  }

  const hasMilestones = milestones.length > 0;
  if (
    hasMilestones &&
    milestones.every((milestone) => milestone.deliveryStatus === 'paid')
  ) {
    return 'paid';
  }

  if (serverWorkflowStatus && serverWorkflowStatus !== 'not_started') {
    return serverWorkflowStatus;
  }

  return projectStatus;
}

export function projectDisplayStatus(
  project: Pick<Project, 'status' | 'workflowStatus'>,
  milestones: Milestone[] = [],
): ProjectDisplayStatus {
  return projectWorkflowStatus(
    project.status,
    milestones,
    project.workflowStatus,
  );
}
